import { useCallback, useEffect, useState } from "react";
import { CalendarPlus, Copy, Pencil, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { templateApi } from "../../../api/templates";
import type { ShiftTemplate } from "../../../api/types";
import { PageHeader } from "../../../components/ComingSoon";
import { Button, DemoBadge, TextInput } from "../../../components/ui";
import { ShiftDrawer } from "../shifts/ShiftDrawer";
import { TemplateFormModal } from "./TemplateFormModal";
import { TemplateRunModal } from "./TemplateRunModal";

const timeText = (t: ShiftTemplate) => (t.start_time_local ? `${t.start_time_local}–${t.end_time_local ?? "?"}${t.ends_next_day ? " (+1)" : ""}` : "No time set");

function Row({ t, onEdit, onRun, onChanged }: { t: ShiftTemplate; onEdit: () => void; onRun: () => void; onChanged: () => void }) {
  const act = async (fn: () => Promise<unknown>, msg: string) => {
    try { await fn(); toast.success(msg); onChanged(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const remove = () => {
    const verb = t.used_count > 0 ? "Archive" : "Delete";
    if (window.confirm(`${verb} template “${t.name}”?`)) act(() => templateApi.remove(t.id), t.used_count > 0 ? "Template archived" : "Template deleted");
  };
  return (
    <li data-testid={`template-row-${t.id}`} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center">
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 text-white"><span className="truncate font-medium" data-testid={`template-name-${t.id}`}>{t.name}</span><DemoBadge show={t.is_demo} testId={`template-demo-${t.id}`} /></p>
        <p className="mt-0.5 text-xs text-zinc-400">{t.role ?? "—"} · <span className="font-mono">{timeText(t)}</span> · {t.default_employee_ids.length} default crew · used {t.used_count}×</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button className="h-9 px-4 text-[11px]" onClick={onRun} data-testid={`template-run-${t.id}`}><CalendarPlus className="h-4 w-4" /> Create shifts</Button>
        <Button variant="secondary" className="h-9 px-3 text-[11px]" onClick={onEdit} aria-label="Edit" data-testid={`template-edit-${t.id}`}><Pencil className="h-4 w-4" /></Button>
        <Button variant="secondary" className="h-9 px-3 text-[11px]" onClick={() => act(() => templateApi.duplicate(t.id), "Template duplicated")} aria-label="Duplicate" data-testid={`template-duplicate-${t.id}`}><Copy className="h-4 w-4" /></Button>
        <Button variant="ghost" className="h-9 px-3 text-[11px] hover:text-signal" onClick={remove} aria-label={t.used_count > 0 ? "Archive" : "Delete"} data-testid={`template-remove-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
    </li>
  );
}

export default function AdminTemplates() {
  const [q, setQ] = useState("");
  const [list, setList] = useState<ShiftTemplate[] | null>(null);
  const [editing, setEditing] = useState<ShiftTemplate | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [running, setRunning] = useState<ShiftTemplate | null>(null);
  const [openShift, setOpenShift] = useState<string | null>(null);
  const load = useCallback(() => { templateApi.list(q || undefined).then(setList).catch(() => setList([])); }, [q]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  return (
    <div data-testid="admin-templates-page">
      <PageHeader title="Templates">
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="template-create-button">New template</Button>
      </PageHeader>
      <div className="relative mb-6 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
        <TextInput className="pl-9" placeholder="Search templates" value={q} onChange={(e) => setQ(e.target.value)} data-testid="template-search-input" />
      </div>
      {list && list.length === 0 && <p data-testid="templates-empty" className="rounded-sm border border-white/10 px-5 py-10 text-center text-sm text-zinc-400">No templates yet. Create one to reuse a venue, role and time.</p>}
      {list && list.length > 0 && <ul data-testid="templates-list" className="divide-y divide-white/5 rounded-sm border border-white/10 bg-surface/60">{list.map((t) => (
        <Row key={t.id} t={t} onEdit={() => { setEditing(t); setFormOpen(true); }} onRun={() => setRunning(t)} onChanged={load} />
      ))}</ul>}
      <TemplateFormModal open={formOpen} editing={editing} onClose={() => setFormOpen(false)} onSaved={() => { setFormOpen(false); load(); }} />
      <TemplateRunModal open={!!running} template={running} onClose={() => setRunning(null)} onDone={load} onOpenShift={(id) => { setRunning(null); setOpenShift(id); }} />
      <ShiftDrawer id={openShift} reload={0} onClose={() => setOpenShift(null)} onEdit={() => undefined} onChanged={load} />
    </div>
  );
}
